/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo } from 'react';

import { cssMap as cssMapCompiled } from '@compiled/react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import SearchIcon from '@atlaskit/icon/core/search';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import { SainAction } from '../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../common/controller/analytics/hooks/use-sain-usage';
import { OpenChatButton } from '../../common/ui/open-chat/open-chat-button';
import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import { useAIAnswerContext } from '../ai-answer-context';
import { onErrorStateSuggestedQueriesShown } from '../on-error-state-suggested-queries-shown';
import { onErrorStateSuggestedQueryClick } from '../on-error-state-suggested-query-click';

const stylesCompiled = cssMapCompiled({
	pillLineHeight: {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
		lineHeight: '16px',
	},
});

const styles = cssMap({
	button: {
		color: token('color.text.subtle'),
		paddingTop: token('space.075'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.150'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		flexShrink: 1,
		backgroundColor: token('color.background.neutral.subtle'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},

	queryText: {
		wordBreak: 'break-word',
		textAlign: 'left',
		'&::first-letter': {
			textTransform: 'capitalize',
		},
	},

	whiteSpacePreWrap: {
		whiteSpace: 'pre-wrap',
	},
	luminaFollowUpButtonStyleOverrides: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
		minWidth: '0px',
		flex: '0 1 auto',
	},
	luminaFollowUpButtonStyleOverridesImproved: {
		backgroundColor: token('elevation.surface'),
	},
	luminaFollowUpButtonNoShrink: {
		flex: '0 0 auto',
	},
	pillContainer: {
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'center',
		overflow: 'hidden',
		flexWrap: 'nowrap',
	},
	pillContainerInner: {
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'center',
		overflow: 'hidden',
		flexShrink: 1,
		minWidth: '0px',
	},
	upliftWrapper: {
		width: '100%',
	},
});

const motionStyles = cssMap({
	button: {
		transition: token('motion.button.hovered'),
		'&:active': {
			transition: token('motion.button.pressed'),
		},
	},
});

export const FollowUpSuggestionFooter: () => JSX.Element[] | null = () => {
	const {
		commonAttributes,
		fireAnalyticsEvent,
		setQuery,
		onClose,
		closeParentDialog,
		showChatFollowUps,
	} = useAIAnswerContext();
	const { query, answerString, errorStateSuggestedQueries } = commonAttributes;
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const isSainOpenChatButtonRemoved = fg('kd_sain_remove_open_chat_button');
	const isImprovedChatOptions = SAINFeatureControl.isImprovedChatOptionsEnabled();

	const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
		abTestId: 'kd_sain_show_no_answer_suggestions_exp',
	});

	// LLM-generated follow-ups are permanently disabled, so the no-answer suggested queries are
	// the only suggestions this footer can show.
	const visibleFollowUps = useMemo(
		() => (serveErrorStateSuggestedQueriesExp ? errorStateSuggestedQueries.slice(0, 2) : []),
		[errorStateSuggestedQueries, serveErrorStateSuggestedQueriesExp],
	);

	const showErrorStateSuggestedQueries = visibleFollowUps.length > 0;

	const onSuggestedQueryClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>, suggestedQuery: string) => {
			e.preventDefault();
			e.stopPropagation();

			setQuery(suggestedQuery);
			fireAnalyticsEvent(
				onErrorStateSuggestedQueryClick({
					...commonAttributes,
					errorStateSuggestedQuery: suggestedQuery,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.ERROR_STATE_SUGGESTED_QUERY_CLICKED,
				commonAttributes,
			});
			onClose?.();
			closeParentDialog?.();
		},
		[
			setQuery,
			fireAnalyticsEvent,
			fireSAINUsageEvent,
			onClose,
			closeParentDialog,
			commonAttributes,
		],
	);

	useEffect(() => {
		if (showErrorStateSuggestedQueries) {
			fireAnalyticsEvent(
				onErrorStateSuggestedQueriesShown({
					...commonAttributes,
					showErrorStateSuggestedQueries: visibleFollowUps,
				}),
			);
		}
	}, [showErrorStateSuggestedQueries, fireAnalyticsEvent, commonAttributes, visibleFollowUps]);

	if (showChatFollowUps) {
		if (isSainOpenChatButtonRemoved) {
			return [];
		}

		const openChatButton = (
			<OpenChatButton prompt={query} query={query} answerString={answerString} source="sain" />
		);

		if (isImprovedChatOptions) {
			return [
				<Box xcss={styles.upliftWrapper} key="uplift-wrapper">
					<Box xcss={styles.pillContainer}>
						<Box xcss={styles.pillContainerInner}>
							<Box
								data-pill
								xcss={cx(
									styles.luminaFollowUpButtonStyleOverrides,
									styles.luminaFollowUpButtonNoShrink,
									styles.luminaFollowUpButtonStyleOverridesImproved,
								)}
							>
								{openChatButton}
							</Box>
						</Box>
					</Box>
				</Box>,
			];
		}

		return [
			<Box
				xcss={cx(styles.luminaFollowUpButtonStyleOverrides, styles.luminaFollowUpButtonNoShrink)}
				key="open-chat"
			>
				{openChatButton}
			</Box>,
		];
	}

	return showErrorStateSuggestedQueries
		? visibleFollowUps.map((followUp) => (
				<Inline space="space.050" alignBlock="center" key={followUp}>
					<Pressable
						xcss={cx(
							styles.button,
							fg('platform-dst-motion-uplift-custom-button') && motionStyles.button,
						)}
						onClick={(e) => onSuggestedQueryClick(e, followUp)}
					>
						<div css={stylesCompiled.pillLineHeight}>
							<Inline space="space.075" alignBlock="baseline">
								<SearchIcon color="currentColor" label="" />
								<Box xcss={cx(styles.queryText, styles.whiteSpacePreWrap)}>{followUp}</Box>
							</Inline>
						</div>
					</Pressable>
				</Inline>
			))
		: null;
};
